<template>
  <v-card class="public-tournament-register" max-width="500px">
    <!-- Improved header layout that won't wrap -->
    <v-card-title class="register-header">
      <div class="d-flex align-center">
        <v-icon color="black" class="mr-2">fa-trophy</v-icon>
        <h2 class="title-text">{{ $t('registerTitle') }}</h2>
      </div>
      <v-btn icon class="close-btn" @click="$emit('close')">
        <v-icon>fa-regular fa-times</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="register-content">
      <div v-if="!formSubmitted" class="register-form-container">
        <p class="mb-4 form-description">{{ $t('registerDescription') }}</p>

        <v-form ref="form" v-model="valid" @submit.prevent="submitForm">
          <!-- Responsive name fields - stacking on very small screens -->
          <div class="name-fields-container">
            <v-text-field
              v-model="firstName"
              :label="$t('firstName')"
              :rules="nameRules"
              required
              outlined
              dense
              class="name-field"
            ></v-text-field>
            
            <v-text-field
              v-model="lastName"
              :label="$t('lastName')"
              :rules="nameRules"
              required
              outlined
              dense
              class="name-field"
            ></v-text-field>
          </div>

          <v-text-field
            v-model="email"
            :label="$t('email_PublicTournamentRegister')"
            :rules="emailRules"
            required
            outlined
            dense
          ></v-text-field>

          <v-autocomplete
            v-model="country"
            :items="countryItems"
            :label="$t('country')"
            :rules="countryRules"
            item-text="nativeName"
            item-value="name"
            required
            outlined
            dense
            @change="updatePhoneCode"
          >
            <template v-slot:selection="data">
              {{ data.item.nativeName }}
            </template>
            <template v-slot:item="data">
              <span>{{ data.item.nativeName }}</span>
              <small class="ml-2 text-caption grey--text">({{ data.item.name }})</small>
            </template>
          </v-autocomplete>

          <v-text-field
            v-model="phone"
            :label="$t('phone')"
            :placeholder="phoneCode ? `${phoneCode} xxxxxxxx` : '+xx xxxxxxxx'"
            :rules="phoneRules"
            :prefix="phoneCode"
            outlined
            dense
          ></v-text-field>

          <!-- Account Login (CXM LIVE only) -->
          <v-text-field
            v-if="showAccountLoginField"
            v-model="accountLogin"
            :label="$t('accountLogin')"
            :rules="accountLoginRules"
            type="number"
            outlined
            dense
          ></v-text-field>

          <v-checkbox
            v-model="termsAccepted"
            color="darkGreen"
            class="terms-checkbox"
            :rules="[v => !!v || $t('termsRequired')]"
            required
          >
            <template v-slot:label>
              <div class="terms-text">
                {{ $t('termsCheckbox') }}
                <a :href="termsConditionUrl" target="_blank" rel="noopener noreferrer" @click.stop>{{ $t('termsLink') }}</a>
              </div>
            </template>
          </v-checkbox>

          <div class="submit-btn-container">
            <v-btn
              :disabled="!valid || loading"
              :loading="loading"
              color="mainGreen"
              class="white--text submit-btn"
              type="submit"
            >
              {{ $t('registerButton') }}
            </v-btn>
          </div>
        </v-form>
      </div>      <!-- Success view - consistent with TournamentEmbedRegister -->
      <div v-else class="success-view">
        <div class="success-animation">
          <i class="fa-solid fa-check-circle success-icon"></i>
        </div>

        <h2 class="success-title">{{ $t('registrationSuccessful') }}</h2>
        <p class="success-description">{{ $t('registrationSuccessDescription') }}</p>

        <!-- User Info Summary -->
        <div class="user-info-card">
          <h3 class="info-title">{{ $t('yourInformation') }}</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">{{ $t('name') }}:</span>
              <span class="info-value">{{ firstName }} {{ lastName }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ $t('email_PublicTournamentRegister') }}:</span>
              <span class="info-value">{{ email }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ $t('phone') }}:</span>
              <span class="info-value">{{ fullPhoneNumber }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ $t('country') }}:</span>
              <span class="info-value">{{ country }}</span>
            </div>
          </div>
        </div>

        <!-- Email Instructions -->
        <div class="email-instructions">
          <div class="instructions-card">
            <i class="fa-solid fa-envelope instructions-icon"></i>
            <h4 class="instructions-title">{{ $t('checkYourEmail') }}</h4>
            <p class="instructions-text">{{ $t('emailInstructionsDescription') }}</p>
          </div>
        </div>        <!-- Action Buttons -->
        <div class="success-actions">
          <v-btn color="mainGreen" class="action-btn" @click="handleOkClick">
            {{ $t('ok') }}
          </v-btn>
        </div>
      </div>
    </v-card-text>    <!-- Snackbar for errors -->
    <v-snackbar v-model="errorSnackbar" :timeout="5000" color="error">
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="errorSnackbar = false">
          {{ $t('close_PublicTournamentRegister') }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import CountriesData from "@/views/Register/countries";
import TournamentRepository from "@/repositories/tournament";
import { getRecaptchaToken } from '@/services/recaptcha';

export default {
  name: 'PublicTournamentRegister',
  props: {
    tournamentId: {
      type: Number,
      required: true
    },
    tournamentData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      valid: false,
      formSubmitted: false,
      loading: false,      errorSnackbar: false,
      errorMessage: '',

      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      country: '',
      phoneCode: '',
      accountLogin: '',
      termsAccepted: false,

      countries: CountriesData,

      nameRules: [
        v => !!v || this.$t('fieldRequired')
      ],
      emailRules: [
        v => !!v || this.$t('fieldRequired'),
        v => /.+@.+\..+/.test(v) || this.$t('emailInvalid')
      ],
      phoneRules: [
        // Updated to allow "+" character for international format
        v => !v || /^[+0-9\s]*$/.test(v) || this.$t('phoneInvalid')
      ],      countryRules: [
        v => !!v || this.$t('fieldRequired')
      ],
      accountLoginRuleBase: v => !v || /^\d+$/.test(v) || this.$t('mustBeNumeric'),
      accountLoginRequired: v => !!v || this.$t('fieldRequired')
    }
  },
  computed: {
    termsConditionUrl() {
      const url = this.tournamentData?.tournamentSettings?.termsCondition;
      return (url && typeof url === 'string' && url.trim() !== '') ? url.trim() : 'https://swiset.com/competitions/terms-and-conditions';
    },
    countryItems() {
      return this.countries.map(country => ({
        name: country.name,
        nativeName: this.$i18n.locale === 'es' ?
          this.translateCountryToSpanish(country.name) :
          country.name,
        code: country.code,
        dial_code: country.dial_code
      }));
    },
    fullPhoneNumber() {
      if (!this.phone) return '';

      // Strip any non-numeric characters except "+" from phone input
      const cleanedPhone = this.phone.replace(/[^\d+]/g, '');

      // Use the selected country's dial code or an empty string if none selected
      let dialCode = this.phoneCode || '';

      // Ensure dialCode starts with +
      if (dialCode && !dialCode.startsWith('+')) {
        dialCode = `+${dialCode}`;
      }

      // If phone already has + prefix or it's the same as dialCode, don't add dialCode
      if (cleanedPhone.startsWith('+') || (dialCode && cleanedPhone.startsWith(dialCode.replace('+', '')))) {
        return cleanedPhone;
      }

      // If phone has digits and we have a dial code, combine them
      if (cleanedPhone && dialCode) {
        return `${dialCode}${cleanedPhone}`;
      }

      // If phone has digits but no dial code, ensure it starts with +
      if (cleanedPhone && !dialCode && !cleanedPhone.startsWith('+')) {
        return `+${cleanedPhone}`;
      }

      return cleanedPhone;
    },
    showAccountLoginField() {
      if (!this.tournamentData) return false;

      // Check for CXM partner at root level or in tournamentSettings
      const partnerId = this.tournamentData.mandatoryPartner?.id ||
                       this.tournamentData.tournamentSettings?.mandatoryPartner?.id;

      const accountType = this.tournamentData.tournamentSettings?.restrictedAccountType;

      return (partnerId === 71 || partnerId === 36) && accountType === 'LIVE';
    },
    accountLoginRules() {
      if (this.showAccountLoginField) {
        return [this.accountLoginRequired, this.accountLoginRuleBase];
      }
      return [];
    }
  },
  methods: {
    updatePhoneCode() {
      if (this.country) {
        const selectedCountry = this.countries.find(c => c.name === this.country);
        let dialCode = selectedCountry ? selectedCountry.dial_code : '';

        // Make sure dialCode has the + symbol
        if (dialCode && !dialCode.startswith('+')) {
          dialCode = `+${dialCode}`;
        }

        this.phoneCode = dialCode;
      } else {
        this.phoneCode = '';
      }
    },
    translateCountryToSpanish(englishName) {
      // Translation mapping for common countries (add more as needed)
      const translations = {
        'Spain': 'España',
        'United States': 'Estados Unidos',
        'United Kingdom': 'Reino Unido',
        'Mexico': 'México',
        'Brazil': 'Brasil',
        'France': 'Francia',
        'Germany': 'Alemania',
        'Italy': 'Italia',
        'Portugal': 'Portugal',
        'Argentina': 'Argentina',
        'Colombia': 'Colombia',
        'Chile': 'Chile',
        'Peru': 'Perú',
        'Venezuela': 'Venezuela',
        'Netherlands': 'Países Bajos',
        'Belgium': 'Bélgica',
        'Switzerland': 'Suiza',
        'Canada': 'Canadá',
        'Australia': 'Australia',
        'Japan': 'Japón',
        'China': 'China',
        'India': 'India',
        'Russia': 'Rusia',
        'Sweden': 'Suecia',
        'Norway': 'Noruega',
        'Denmark': 'Dinamarca',
        'Finland': 'Finlandia',
        'Greece': 'Grecia',
        'Turkey': 'Turquía',
        'Poland': 'Polonia',
        'Austria': 'Austria',
      };      return translations[englishName] || englishName;
    },

    async submitForm() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      try {
        // Get reCAPTCHA token - using same action as register flow for consistency
        const recaptchaToken = await getRecaptchaToken('register');

        // Prepare payload for API
        const payload = {
          firstName: this.firstName,
          lastName: this.lastName,
          country: this.country, // Always send English country name
          phoneNumber: this.fullPhoneNumber, // This will now always include "+" prefix
          email: this.email,
          tournamentId: this.tournamentId,
          langKey: this.$i18n.locale,
          recaptchaToken: recaptchaToken
        };

        // Add accountLogin only for CXM LIVE tournaments
        if (this.showAccountLoginField && this.accountLogin) {
          payload.accountLogin = this.accountLogin;
        }

        // Call the API
       const response = await TournamentRepository.registerPublicTournament(payload);

        // Check for success flag in response
        if (response.data && response.data.success) {
          // Show success state
          this.formSubmitted = true;
          this.accountLogin = response.data.accountLogin;

          // Don't emit registered event immediately - wait for user to click OK
        } else {
          // Show error message from API
          this.errorMessage = response.data.message || this.$t('registerError');
          this.errorSnackbar = true;
        }
      } catch (error) {
        // Check if it's a 404 error and accountLogin field was provided
        if (error?.response?.status === 404 && this.showAccountLoginField && this.accountLogin) {
          this.errorMessage = this.$t('accountLoginNotFound');
        } else {
          this.errorMessage = error?.response?.data?.message || this.$t('registerError');
        }
        this.errorSnackbar = true;
      } finally {
        this.loading = false;
      }
    },
    
    handleOkClick() {
      // Emit the registered event with user data
      this.$emit('registered', {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        identifier: this.accountLogin
      });
      
      // Close the modal
      this.$emit('close');
    }
  },
  beforeDestroy() {
    // Clear any active timers
    if (this.redirectTimer) {
      clearInterval(this.redirectTimer);
    }
  },
  mounted() {
    // Set default country if user has one stored
    if (this.$store.state.user && this.$store.state.user.details && this.$store.state.user.details.country) {
      this.country = this.$store.state.user.details.country;
      this.updatePhoneCode();
    }
  }
}
</script>

<style lang="scss" scoped>
.public-tournament-register {
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  background: var(--v-mainWhite-base);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
  
  // Improved header with proper positioning
  .register-header {
    padding: 16px 16px 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    position: relative;
    
    .title-text {
      font-size: 18px;
      font-weight: 600;
      margin: 0;
      line-height: 1.4;
      white-space: nowrap;
      max-width: calc(100% - 40px); // Leave space for the close button
    }
    
    .close-btn {
      margin-right: -8px;
    }
  }
  
  // Content area with better padding
  .register-content {
    padding: 16px 20px 24px;
  }
  
  // Responsive form styles
  .register-form-container {
    .form-description {
      font-size: 14px;
      color: #666;
      margin-bottom: 20px;
    }
    
    // Responsive name fields
    .name-fields-container {
      display: flex;
      gap: 12px;
      width: 100%;
      
      .name-field {
        width: 50%;
      }
    }
    
    .terms-checkbox {
      margin-top: 8px;
      
      .terms-text {
        font-size: 13px;
        line-height: 1.4;
        
        a {
          color: var(--v-mainGreen-base);
          text-decoration: none;
          font-weight: 500;
        }
      }
    }
    
    .submit-btn-container {
      display: flex;
      justify-content: center;
      margin-top: 16px;
      
      .submit-btn {
        min-width: 160px;
        height: 42px;
      }
    }
  }
    // Success view styling - consistent with TournamentEmbedRegister
  .success-view {
    text-align: center;
    padding: 20px 16px;
    
    .success-animation {
      margin-bottom: 24px;
      
      .success-icon {
        font-size: 64px;
        color: var(--v-mainGreen-base);
        animation: success-bounce 0.8s ease-out;
      }
    }
    
    .success-title {
      font-size: 24px;
      font-weight: 600;
      color: var(--v-mainBlack-base);
      margin-bottom: 8px;
    }
    
    .success-description {
      font-size: 16px;
      color: #666;
      margin-bottom: 24px;
      line-height: 1.5;
    }
    
    // User info card
    .user-info-card {
      background-color: #f8f9fa;
      border-radius: 12px;
      padding: 20px;
      margin-bottom: 24px;
      border: 1px solid #e9ecef;
      
      .info-title {
        font-size: 18px;
        font-weight: 600;
        color: var(--v-mainBlack-base);
        margin-bottom: 16px;
        text-align: center;
      }
      
      .info-grid {
        display: grid;
        gap: 12px;
        text-align: left;
        
        .info-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #e9ecef;
          
          &:last-child {
            border-bottom: none;
          }
          
          .info-label {
            font-weight: 500;
            color: #495057;
            font-size: 14px;
          }
          
          .info-value {
            font-weight: 600;
            color: var(--v-mainBlack-base);
            font-size: 14px;
            text-align: right;
            word-break: break-word;
          }
        }
      }
    }
      // Email instructions
    .email-instructions {
      margin-bottom: 24px;
      
      .instructions-card {
        background: #e8f5e8;
        border-radius: 12px;
        padding: 20px;
        border-left: 4px solid var(--v-mainGreen-base);
        text-align: center;
        
        .instructions-icon {
          font-size: 32px;
          color: var(--v-mainGreen-base);
          margin-bottom: 12px;
        }
        
        .instructions-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--v-mainBlack-base);
          margin-bottom: 8px;
        }
        
        .instructions-text {
          font-size: 14px;
          color: #666;
          line-height: 1.5;
          margin: 0;
        }
      }
    }
      // Success actions
    .success-actions {
      display: flex;
      justify-content: center;
      
      .action-btn {
        min-width: 120px;
        height: 44px;
        font-weight: 600;
        text-transform: none;
        letter-spacing: 0.5px;
      }
    }
  }
  
  @keyframes success-bounce {
    0% { 
      transform: scale(0.3); 
      opacity: 0; 
    }
    50% { 
      transform: scale(1.1); 
      opacity: 0.8; 
    }
    100% { 
      transform: scale(1); 
      opacity: 1; 
    }
  }
  
  // Confetti animation for success state
  .confetti-container {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    pointer-events: none;
    
    .confetti-item {
      position: absolute;
      width: 8px;
      height: 16px;
      top: 30px;
      left: 50%;
      opacity: 0;
      
      @for $i from 1 through 20 {
        &.confetti-#{$i} {
          transform: rotate(random(360) + deg);
          background-color: nth(#FFD700 #87CEEB #FF6347 #32CD32 #FF1493, ($i % 5) + 1);
          animation: confetti-fall-#{$i} random(3) + 1s ease-out forwards;
          
          @keyframes confetti-fall-#{$i} {
            0% {
              opacity: 1;
              top: 30px;
              left: 50%;
            }
            100% {
              opacity: 0;
              top: random(150) + 50px;
              left: percentage(random(100) / 100);
            }
          }
        }
      }
    }
  }
  
  @keyframes trophy-bounce {
    0% { transform: scale(0.5); opacity: 0; }
    70% { transform: scale(1.2); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }
}

// Responsive styles for mobile
@media (max-width: 600px) {
  .public-tournament-register {
    border-radius: 8px;
    margin: 0;
    
    .register-header {
      padding: 16px 16px 12px;
      
      .title-text {
        font-size: 17px;
      }
    }
    
    .register-content {
      padding: 12px 16px 20px;
    }
    
    .register-form-container {
      .form-description {
        font-size: 13px;
        margin-bottom: 16px;
      }
      
      // Stack name fields on very small screens
      @media (max-width: 340px) {
        .name-fields-container {
          flex-direction: column;
          gap: 8px;
          
          .name-field {
            width: 100%;
          }
        }
      }
      
      .terms-checkbox {
        margin-top: 4px;
        
        .terms-text {
          font-size: 12px;
        }
      }
      
      .submit-btn-container {
        .submit-btn {
          width: 100%;
          height: 48px; // Larger height for better tap target
        }
      }
    }
    
    // Smaller success view for mobile
    .success-view {
      .success-animation {
        height: 70px;
        margin: 4px 0 16px;
      }
      
      .success-title {
        font-size: 18px;
      }
      
      .success-description {
        font-size: 13px;
      }
      
      .public-link-wrapper {
        padding: 12px;
      }
    }
  }
}
</style>


